import ApiCaller from "api/apiCaller";
import Loading from "components/Loading";
import ModalIFRSReport from "components/ModalIFRSReport";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRouteMatch, useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import { Button } from "shared";
import { getContractLocation } from "utils/SiteHelper";
import "./index.scss";
import numeral from "numeral";
import IFRSLeaseTable from "components/IFRSLeaseTable";
import ModalPaymentSchedule from "components/ModalPaymentSchedule";
import { LISA } from "types";
import _ from "lodash";

const IFRSDetail = () => {
  const [isOpenIFRS, setOpenIFRS] = useState(false);
  const [isOpenPaymentSchedule, setOpenPaymentSchedule] = useState(false);

  const toggleIFRS = useCallback(() => setOpenIFRS(!isOpenIFRS), [isOpenIFRS]);
  const togglePaymentSchedule = useCallback(() => {
    setOpenPaymentSchedule((current) => !current);
  }, []);

  const history = useHistory();
  const match = useRouteMatch("/finance/ifrs/:contract_id");

  const params: any = match?.params;
  const contractId = params?.contract_id;

  const [leaseData, setLeaseData] = useState<{
    ifrs_lease_result_calculation: LISA.IFRSLeaseResultCalcApiData | null;
    data: Array<LISA.IFRSLeaseResultApiData>;
  }>({
    ifrs_lease_result_calculation: null,
    data: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [contractData, setContractData] = useState<LISA.ContractApiData | null>(null);
  const [error, setError] = useState('');

  const fetchLeaseData = useCallback(async () => {
    if (!contractData?.site_id) {
      return
    }

    const leaseRes = await ApiCaller.get(`ifrs_lease/site/${contractData.site_id}`);
    if (leaseRes.statusCode === 200) {
      setLeaseData({
        ifrs_lease_result_calculation: leaseRes.ifrs_lease_result_calculation,
        data: leaseRes.ifrs_lease_result,
      });
    }
  }, [contractData?.site_id]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    // setContractData({ data: null, loading: true, error: "" });
    const res = await ApiCaller.get(`contract/${contractId}`);
    if (res.result) {
      setContractData(res.result);
      fetchLeaseData();
    } else if (res.message) {
      setError(res.message);

    }
    // setContractData({ data: res?.result, loading: false, error: res?.message });
    // if (res?.result) fetchLeaseData(res?.result);

    setIsLoading(false);
  }, [contractId, fetchLeaseData]);

  useEffect(() => {
    if (contractId) {
      fetchData();
    }
  }, [contractId, fetchData]);

  const onRunPaymentSchedule = useCallback(
    async (data: {from_date: string; to_date: string}) => {
      const res = await ApiCaller.post(
        `site_lease_payment_schedule/site/${contractData?.site?.id}?from_date=${data.from_date}&to_date=${data.to_date}`
      );
      if (res.status === "ok") {
        togglePaymentSchedule();
        alert("Tạo chu kỳ thanh toán thành công");
      } else {
        alert(res.message || "Có lỗi xảy ra");
      }
    },
    [togglePaymentSchedule, contractData?.site?.id]
  );
  const saveStreamCSV = useCallback((filename, text) => {
    const anchor = document.createElement("a");
    anchor.download = filename;
    if (window.URL.createObjectURL) {
      // Everything else new.
      const blobObject = new Blob([text], { type: "text/csv" });
      anchor.href = window.URL.createObjectURL(blobObject);
    } else {
      // Fallback for older browsers (limited to 2MB on post-2010 Chrome).
      // Load up the data into the URI for "download."
      anchor.href = "data:text/csv;charset=utf-8," + encodeURIComponent(text);
    }
    // Now, click it.
    if (document.createEvent) {
      const event = document.createEvent("MouseEvents");
      event.initEvent("click", true, true);
      anchor.dispatchEvent(event);
    } else {
      anchor.click();
    }
  }, []);

  const handleExportClick = useCallback(async () => {
    const res = await ApiCaller.post(
      `ifrs_lease/${leaseData?.ifrs_lease_result_calculation?.id}/export/csv`,
      undefined,
      undefined,
      {
        Accept: "text/csv",
      },
      true
    );
    saveStreamCSV(
      `ifrs_lease_result_calculation_${leaseData?.ifrs_lease_result_calculation?.id}`,
      res
    );
  }, [leaseData?.ifrs_lease_result_calculation?.id, saveStreamCSV]);
  
  const onSiteRevenue = useCallback(() => {
    history.push(`/finance/revenue/${contractData?.site?.id}`);
  }, [contractData?.site?.id, history]);

  const onContractDetail = useCallback(() => {
    history.push(`/contract/${contractData?.site?.id}`);
  }, [contractData?.site?.id, history]);

  const currentIdx = useMemo(() => {
    return contractData?.payment_terms?.findIndex((el: any) => moment(moment(new Date())).isBetween(el.from_date, el.to_date));
  }, [contractData?.payment_terms]);

  const currentPayment = useMemo(() => {
    return _.get(contractData?.payment_terms, currentIdx);
  }, [contractData?.payment_terms, currentIdx]);
  const nextPayment = useMemo(() => {
    return _.get(contractData?.payment_terms, currentIdx + 1);
  }, [contractData?.payment_terms, currentIdx]);

  const getPayment = useCallback((payment: any) => {
    if (payment.revenue_share_percentage) {
      return `${payment.revenue_share_percentage * 100} %`;
    }
    return numeral(payment.rental_fee).format("0,0[.]000");
  }, []);

  const getProgress = useCallback(() => {
    const { contract_valid_from, contract_valid_to } = contractData;
    const diff1 = moment(moment(new Date())).diff(contract_valid_from, "days");
    const diff2 = moment(contract_valid_to).diff(moment(new Date()), "days");
    let res = Math.round((100 * diff1) / diff2);
    if (res < 1) {
      res = 1;
    }
    return res;
  }, [contractData]);


  const diffDays = useCallback(() => {
    if (!nextPayment) {
      return "0";
    }

    return moment(nextPayment.from_date).diff(moment(new Date()), "days");
  }, [nextPayment]);
  const onRunReport = useCallback(async (data: any) => {
    const res = await ApiCaller.post(
      `ifrs_lease/site/${contractData.site.id}`,
      data
    );
    if (res.status === "ok") {
      toggleIFRS();
      fetchLeaseData();
    } else {
      alert(res.message);
    }
  }, [contractData, fetchLeaseData, toggleIFRS]);

  const handleViewSiteClick = useCallback(() => {
    history.push(`/sitev2/${contractData?.site_id}`);
  }, [contractData?.site_id, history]);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return (
      <div className="ifrs-detail__container">
        <span>{error}</span>
      </div>
    );
  }

  return (
    <Container style={{ height: "100%" }}>
      <div className="ifrs-detail__container">
        <div className="page-header">
          <div className="head-info">
            <span className="page-title">{contractData?.name}</span>
            <span className="page-description">
              Địa chỉ:{" "}
              <span className="page-description-value">
                {getContractLocation(contractData?.store)}
              </span>
            </span>
          </div>
          <div className="page-action">
            <Button
              label="Payment Schedule"
              type="primary"
              onClick={togglePaymentSchedule}
            />
            <Button
              label="Run IFRS Report"
              type="primary"
              onClick={toggleIFRS}
            />
            <Button
              label="Quản lý doanh thu CH"
              type="primary"
              onClick={onSiteRevenue}
            />
            <Button
              label="Xem chi tiết HĐ"
              type="primary"
              onClick={onContractDetail}
            />
            <Button
              label="Xem chi tiết MB"
              type="primary"
              onClick={handleViewSiteClick}
            />
          </div>
        </div>
        <div className="page-body">
          <div className="contract-info">
            <div className="expire-range__wrap">
              <div className="progress__wrap">
                <div
                  className="progress"
                  style={{
                    width: `${getProgress()}%`,
                  }}
                />
              </div>
              <div className="date__wrap">
                <span className="date">
                  {contractData.contract_valid_from}
                </span>
                <span className="date">
                  {contractData.contract_valid_to}
                </span>
              </div>
            </div>
            {currentPayment && (
              <div className="col-info">
                <span className="label">Current Payment</span>
                <span className="value">{getPayment(currentPayment)}</span>
                <span className="date">{currentPayment.from_date}</span>
              </div>
            )}
            {nextPayment && (
              <div className="col-info">
                <span className="label">Next Payment</span>
                <span className="value">{getPayment(nextPayment)}</span>
                <span className="date">{nextPayment.from_date}</span>
              </div>
            )}
            <div className="col-info">
              <span className="label">Payment Remaining</span>
              <span className="value">{diffDays()}</span>
            </div>
          </div>
          {leaseData?.ifrs_lease_result_calculation && (
            <div className="lease-report-info">
              <span className="version">
                Version: {leaseData?.ifrs_lease_result_calculation?.version}
                <br />
                <span className="version-note">
                  Note:{" "}
                  {
                    leaseData?.ifrs_lease_result_calculation
                      ?.version_calculation_note
                  }
                </span>
              </span>
              <div style={{ flex: 1 }} />
              <Button
                label="Export"
                type="primary"
                onClick={handleExportClick}
              />
            </div>
          )}
          {leaseData?.data?.length > 0 && (
            <IFRSLeaseTable data={leaseData.data} />
          )}
        </div>
      </div>
      <ModalIFRSReport
        isOpen={isOpenIFRS}
        toggle={toggleIFRS}
        startDate={contractData.contract_valid_from}
        endDate={contractData.contract_valid_to}
        onRunReport={onRunReport}
      />
      <ModalPaymentSchedule
        isOpen={isOpenPaymentSchedule}
        toggle={togglePaymentSchedule}
        startDate={contractData.contract_valid_from}
        endDate={contractData.contract_valid_to}
        onRunPaymentSchedule={onRunPaymentSchedule}
        siteId={contractData.site.id as any}
      />
    </Container>
  );
};

export default IFRSDetail;
